import React from 'react';
import ServiceLayout from '../../../components/layout/ServiceLayout';
import Hero from '../../../components/sections/Hero';
import Card from '../../../components/ui/Card';
import FAQ from '../../../components/ui/FAQ';
import CallToAction from '../../../components/sections/CallToAction';
import { faqs, targetAudiences, services } from '../../../data/mockData';
import ServiceCard from '../../../components/ui/ServiceCard';
import FeatureCard from '../../../components/ui/FeatureCard';

const IndividualsPage: React.FC = () => {
  // Get individual audience data
  const individualData = targetAudiences.find(audience => audience.id === 'individuals');
  
  // Filter relevant services for individuals
  const relevantServices = services.filter(service => 
    ['ai-training', 'custom-ai-development'].includes(service.id)
  );

  // Filter relevant FAQs
  const relevantFaqs = faqs.slice(0, 3);
  
  return (
    <ServiceLayout>
      <Hero
        title="AI Solutions for Individual Professionals"
        subtitle="Build your AI skills and supercharge your career with personalized training and support designed for individual success."
        ctaText="Explore Training Options"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Value Proposition Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Stay Ahead in the AI Revolution</h2>
            <p className="text-lg text-gray-700">
              In today's rapidly evolving job market, AI skills are no longer optional—they're essential. 
              Our specialized training and coaching programs help individual professionals like you master 
              AI tools and techniques that will make you more valuable, productive, and competitive in your field.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Career Advancement</h3>
              <p>
                Develop in-demand AI skills that employers are actively seeking, positioning yourself
                for promotions, better opportunities, and higher compensation.
              </p>
            </Card>
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Productivity Multiplier</h3>
              <p>
                Learn to leverage AI tools that can automate routine tasks, enhance your creativity,
                and multiply your productivity in your current role.
              </p>
            </Card>
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Future-Proof Your Career</h3>
              <p>
                Develop adaptable AI skills that will remain valuable as technology evolves,
                ensuring long-term career resilience and growth opportunities.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Solutions for Individuals */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Tailored Solutions for Individual Success</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {individualData && (
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-semibold mb-4">{individualData.title}</h3>
                <p className="mb-6 text-gray-700">{individualData.description}</p>
                <ul className="space-y-3 mb-8">
                  {individualData.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-primary font-bold mr-2">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="bg-primary bg-opacity-5 p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4">Who This Is For</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-xl font-medium mb-2">Career Changers</h4>
                  <p className="text-gray-700">
                    Professionals looking to transition into AI-focused roles or industries 
                    where AI skills are in high demand.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Skill Enhancers</h4>
                  <p className="text-gray-700">
                    Professionals looking to add AI capabilities to their existing skillset 
                    to become more valuable in their current role.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Freelancers & Consultants</h4>
                  <p className="text-gray-700">
                    Independent professionals seeking to offer AI-enhanced services to clients 
                    and command premium rates.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Entrepreneurs</h4>
                  <p className="text-gray-700">
                    Business founders looking to incorporate AI capabilities into their 
                    products, services, or operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Programs */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Individual Training Programs</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              title="AI Fundamentals for Professionals"
              description="A comprehensive introduction to AI concepts, capabilities, and applications across various industries. Perfect for beginners."
              icon="book-open"
              details={[
                "8-week online course",
                "Live weekly workshops",
                "Hands-on projects",
                "Certificate of completion"
              ]}
            />
            <FeatureCard
              title="Role-Specific AI Training"
              description="Specialized training focused on AI applications and tools relevant to your specific profession, with practical, immediately applicable skills."
              icon="briefcase"
              details={[
                "Tailored curriculum",
                "Industry-specific use cases",
                "Tool-specific training",
                "Portfolio-building projects"
              ]}
            />
            <FeatureCard
              title="1-on-1 AI Career Coaching"
              description="Personalized coaching to help you develop an AI skills roadmap, navigate learning resources, and position yourself for career advancement."
              icon="users"
              details={[
                "Personalized skills assessment",
                "Custom learning plan",
                "Bi-weekly coaching sessions",
                "Portfolio and resume review"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Relevant Services Section */}
      {relevantServices.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Our Services for Individual Professionals</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relevantServices.map(service => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Success Metrics */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">The Impact of Our Individual Training</h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">94%</div>
              <p className="text-xl">Of participants report career advancement within 12 months</p>
            </div>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">3.5x</div>
              <p className="text-xl">Average productivity improvement through AI skills</p>
            </div>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">32%</div>
              <p className="text-xl">Average salary increase after completing our programs</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto">
            <FAQ items={relevantFaqs} />
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <CallToAction
        title="Ready to accelerate your career with AI skills?"
        description="Contact us today to discuss your professional goals and how our individual training programs can help you achieve them."
        ctaText="Start Your AI Journey"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default IndividualsPage;